import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

export default class extends Controller {
  static targets = ["canvas", "legend"]
  static values = {
    data: Array,
    series: Array
  }

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
  }

  renderChart() {
    if (!this.hasCanvasTarget || !Array.isArray(this.dataValue) || this.dataValue.length === 0) return
    if (!Array.isArray(this.seriesValue) || this.seriesValue.length === 0) return

    const labels = this.dataValue.map((point) => {
      const date = new Date(point.date)
      return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    })

    const datasets = this.seriesValue
      .map((series) => this.datasetFor(series))
      .filter((dataset) => dataset.data.some((value) => Number.isFinite(value)))

    if (datasets.length === 0) return

    this.chart = new Chart(this.canvasTarget.getContext("2d"), {
      type: "line",
      data: {
        labels: labels,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: "index"
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => `${context.dataset.label}: ${this.formatValue(context.dataset.valueFormat, context.parsed.y)}`
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { autoSkip: true, maxTicksLimit: 8 }
          },
          y: {
            ticks: {
              callback: (value) => this.formatAxisTick(value),
              maxTicksLimit: 6
            },
            grid: {
              color: "rgba(0, 0, 0, 0.05)"
            }
          }
        }
      }
    })

    this.applyDynamicRange()
    this.renderLegend()
  }

  datasetFor(series) {
    const finiteValues = this.dataValue
      .map((point) => point[series.key])
      .filter((value) => Number.isFinite(value))

    const sparseSeries = finiteValues.length <= 2

    return {
      key: series.key,
      label: series.label,
      data: this.dataValue.map((point) => point[series.key] ?? null),
      valueFormat: series.format || "number",
      borderColor: series.color,
      backgroundColor: "transparent",
      borderWidth: 2.4,
      pointRadius: sparseSeries ? 4 : 0,
      pointHoverRadius: sparseSeries ? 5 : 4,
      tension: 0.24
    }
  }

  renderLegend() {
    if (!this.hasLegendTarget || !this.chart) return

    this.legendTarget.innerHTML = ""

    this.chart.data.datasets.forEach((dataset, index) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "gi-legend-button"

      if (!this.chart.isDatasetVisible(index)) {
        button.classList.add("is-muted")
      }

      const dot = document.createElement("span")
      dot.className = "gi-dot"
      dot.style.background = dataset.borderColor

      const label = document.createElement("span")
      label.textContent = dataset.label

      button.append(dot, label)
      button.addEventListener("click", () => this.toggleDataset(index))
      this.legendTarget.appendChild(button)
    })
  }

  toggleDataset(index) {
    if (!this.chart) return

    this.chart.setDatasetVisibility(index, !this.chart.isDatasetVisible(index))
    this.applyDynamicRange()
    this.chart.update()
    this.renderLegend()
  }

  applyDynamicRange() {
    if (!this.chart) return

    const visibleValues = this.chart.data.datasets.flatMap((dataset, index) => {
      if (!this.chart.isDatasetVisible(index)) return []
      return dataset.data.filter((value) => Number.isFinite(value))
    })

    if (visibleValues.length === 0) {
      this.chart.options.scales.y.min = undefined
      this.chart.options.scales.y.max = undefined
      return
    }

    const min = Math.min(...visibleValues)
    const max = Math.max(...visibleValues)
    const spread = max - min
    const magnitude = Math.max(Math.abs(min), Math.abs(max), 1)
    const padding = spread > 0 ? Math.max(spread * 0.12, magnitude * 0.02) : Math.max(magnitude * 0.03, 0.1)

    this.chart.options.scales.y.min = Math.floor((min - padding) * 10) / 10
    this.chart.options.scales.y.max = Math.ceil((max + padding) * 10) / 10
  }

  formatAxisTick(value) {
    const format = this.primaryFormat()

    switch (format) {
      case "percent":
        return `${Number(value).toFixed(2)}%`
      case "usd":
        return `$${Number(value).toFixed(0)}`
      case "index":
        return Number(value).toFixed(0)
      case "number":
      default:
        return Number(value).toFixed(2)
    }
  }

  formatValue(format, value) {
    if (!Number.isFinite(value)) return "n/a"

    switch (format) {
      case "percent":
        return `${value.toFixed(2)}%`
      case "usd":
        return `$${value.toFixed(2)}`
      case "index":
        return value.toFixed(2)
      case "number":
      default:
        return value.toFixed(2)
    }
  }

  primaryFormat() {
    return this.chart?.data?.datasets?.find((dataset, index) => this.chart.isDatasetVisible(index))?.valueFormat || "number"
  }
}
